import axios from "axios";
import { BASEURL } from "../BaseUrl";

/**
 * Retrieves CSV data from a specified path.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {Object} data - The data for retrieving the CSV.
 * @param {string} data.path - The path of the CSV file.
 * @param {string} data.aws_config_type - The AWS configuration type.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const getCsvData = async (axios, data) => {
  return await axios.post(
    `${BASEURL}/file/get-csv-data`,
    { path: data.path, aws_config_type: data.aws_config_type },
    {}
  );
};

/**
 * Fetches custom rules from the API.
 * @param {Object} axios - The Axios instance to use for the request.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const getCustomRule = async (axios) => {
  return await axios.get(`${BASEURL}/custom-rule/get-rules`, {});
};

/**
 * Creates metadata for the rule engine.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} client_id - The ID of the client for which metadata is created.
 * @param {Object} metadata - The metadata to be created.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const createMetadata = async (axios, client_id, metadata) => {
  return await axios.post(
    `${BASEURL}/rule-engine/create-rule-engine-metadata`,
    metadata,
    {
      params: { client_id },
    }
  );
};

/**
 * Updates an existing rule engine with new data.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {Object} data - The data to update the rule engine with.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const UpdateRuleEngine = async (axios, data) => {
  return await axios.put(`${BASEURL}/rule-engine/update-rule-engine`, data, {});
};

/**
 * Executes the rule engine with the provided data.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {Object} data - The data to execute the rule engine with.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const executeRuleEngine = async (axios, data) => {
  return await axios.post(
    `${BASEURL}/rule-engine/execute-rule-engine`,
    data,
    {}
  );
};

/**
 * Fetches the rule engine configuration for a specified workflow.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} workflow_id - The ID of the workflow.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const fetchTableRuleNew = async (axios, workflow_id) => {
  return await axios.get(`${BASEURL}/rule-engine/get-rule-engine`, {
    params: { workflow_id },
  });
};

/**
 * Retrieves a list of files from S3 for a specified client and prefix.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} client_id - The ID of the client.
 * @param {string} prefix - The prefix for filtering files.
 * @param {string} aws_config_type - The AWS configuration type.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const AllFilesOfS3 = async (axios, client_id, prefix, aws_config_type) => {
  return await axios.get(`${BASEURL}/rule-engine/get-files-list`, {
    params: { prefix, client_id, aws_config_type },
  });
};

/**
 * Fetches the rule engine configuration for a specific filename within a workflow.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} workflow_id - The ID of the workflow.
 * @param {string} filename - The filename of the rule engine.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const fetchSingleTableRuleNew = async (axios, workflow_id, filename) => {
  return await axios.get(`${BASEURL}/rule-engine/get-rule-engine-by-filename`, {
    params: { workflow_id, filename },
  });
};

/**
 * Retrieves a list of rule engines associated with a specific workflow.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} workflow_id - The ID of the workflow.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const getRuleEngineList = async (axios, workflow_id) => {
  return await axios.get(`${BASEURL}/rule-engine/get-rule-engine-list`, {
    params: { workflow_id },
  });
};

/**
 * Creates a new rule engine with the provided data.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {Object} data - The data for creating the rule engine.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const createRuleEngineNew = async (axios, data) => {
  return await axios.post(
    `${BASEURL}/rule-engine/create-rule-engine`,
    data,
    {}
  );
};

/**
 * Updates the execution status of a workflow.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {Object} data - The data to update the execution with.
 * @param {string} workflow_id - The ID of the workflow.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const updateExecution = async (axios, data, workflow_id) => {
  return await axios.put(
    `${BASEURL}/execution/update-workflow-execution`,
    data,
    {
      params: { workflow_id },
    }
  );
};

/**
 * Deletes a rule engine configuration by its filename and workflow ID.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} workflow_id - The ID of the workflow.
 * @param {string} filename - The filename of the rule engine to delete.
 * @param {string} aws_config_type - The AWS configuration type.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const deleteRuleEngine = async (
  axios,
  workflow_id,
  filename,
  aws_config_type
) => {
  return await axios.delete(`${BASEURL}/rule-engine/delete-rule-engine`, {
    params: { workflow_id, filename, aws_config_type },
  });
};

export const RuleEngineApi = {
  getCsvData,
  getCustomRule,
  createMetadata,
  UpdateRuleEngine,
  executeRuleEngine,
  fetchTableRuleNew,
  fetchSingleTableRuleNew,
  getRuleEngineList,
  AllFilesOfS3,
  createRuleEngineNew,
  updateExecution,
  deleteRuleEngine,
};
